import React, { useCallback, useMemo, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { IconButton, styled } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { VersionReportPageType } from '../../../../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { VersionReportVersionPageFormModel } from '../../../model/VersionReportVersionPageFormModel'
import { DeletePageConfirmDialog } from './DeletePageConfirmDialog'

export type PageStatus = 'new' | 'edited' | 'unchanged'

const STATUS_COLOR_MAP: Record<
  PageStatus,
  { main: string; bg: string; border: string }
> = {
  new: {
    main: colorPalette.skyBlue[7],
    bg: colorPalette.skyBlue[0],
    border: colorPalette.skyBlue[7],
  },
  edited: {
    main: colorPalette.yellow[4],
    bg: colorPalette.yellow[1],
    border: colorPalette.yellow[2],
  },
  unchanged: {
    main: colorPalette.monotone[4],
    bg: colorPalette.monotone[1],
    border: colorPalette.monotone[4],
  },
}

const PAGE_TYPE_SHORT_LABEL: Record<VersionReportPageType, string> = {
  PROGRESS_OVERVIEW: '概況',
  PROGRESS_REPORT_BY_PROCESS: '工程別',
  PROGRESS_REPORT_BY_TEAM: 'チーム別',
}

type PageThumbnailItemProps = {
  page: VersionReportVersionPageFormModel
  isSelected: boolean
  hasError: boolean
  isUnconfigured: boolean
  status: PageStatus
  onSelect: () => void
  onOpenConfig: () => void
  onDelete: () => void
}

export const PageThumbnailItem = ({
  page,
  isSelected,
  hasError,
  isUnconfigured,
  status,
  onSelect,
  onOpenConfig,
  onDelete,
}: PageThumbnailItemProps) => {
  const shortLabel = PAGE_TYPE_SHORT_LABEL[page.pageType]
  const statusColor = STATUS_COLOR_MAP[status]

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: page.pageNumber,
  })

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition]
  )

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  const handleSettingsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onOpenConfig()
    },
    [onOpenConfig]
  )

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setDeleteConfirmOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false)
    onDelete()
  }, [onDelete])

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false)
  }, [])

  return (
    <ThumbnailItem
      ref={setNodeRef}
      style={style}
      isSelected={isSelected}
      hasError={hasError}
      isDragging={isDragging}
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <AccentBar color={statusColor.main} />

      <TopLeftRow>
        <PageNumberBadge color={statusColor.main}>
          {page.pageNumber}
        </PageNumberBadge>
        <TypeBadge
          color={statusColor.main}
          bg={statusColor.bg}
          border={statusColor.border}
        >
          {shortLabel}
        </TypeBadge>
      </TopLeftRow>

      <ThumbnailContent>
        <PageTitle>{page.pageTitle}</PageTitle>
      </ThumbnailContent>

      <ActionsWrapper onClick={stopPropagation} onPointerDown={stopPropagation}>
        <ActionIconButton
          onClick={handleSettingsClick}
          aria-label="ページ設定"
          size="small"
        >
          <StyledMoreVertIcon />
        </ActionIconButton>
        <ActionIconButton
          onClick={handleDeleteClick}
          aria-label="ページを削除"
          size="small"
        >
          <StyledCloseIcon />
        </ActionIconButton>
      </ActionsWrapper>
      {isUnconfigured && (
        <UnconfiguredBadge>
          <UnconfiguredIcon />
          未設定
        </UnconfiguredBadge>
      )}
      <DeletePageConfirmDialog
        open={deleteConfirmOpen}
        pageNumber={page.pageNumber}
        pageTitle={page.pageTitle}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </ThumbnailItem>
  )
}

const ThumbnailItem = styled('div')<{
  isSelected: boolean
  hasError: boolean
  isDragging?: boolean
}>(({ isSelected, hasError, isDragging }) => {
  const borderColor = hasError
    ? colorPalette.pink[2]
    : isSelected
      ? colorPalette.skyBlue[7]
      : colorPalette.monotone[2]
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '96px',
    paddingLeft: '12px',
    cursor: isDragging ? 'grabbing' : 'grab',
    border: `1.5px solid ${borderColor}`,
    borderRadius: '6px',
    backgroundColor: hasError ? colorPalette.pink[0] : colorPalette.monotone[0],
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow: isSelected ? `0 0 0 3px ${colorPalette.skyBlue[1]}` : 'none',
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    overflow: 'hidden',

    '&:hover': {
      borderColor: hasError ? colorPalette.pink[2] : colorPalette.monotone[3],
    },
  }
})

const AccentBar = styled('div')<{ color: string }>(({ color }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  width: '3px',
  backgroundColor: color,
  pointerEvents: 'none',
}))

const TopLeftRow = styled('div')({
  position: 'absolute',
  top: '6px',
  left: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  zIndex: 1,
  pointerEvents: 'none',
})

const PageNumberBadge = styled('div')<{ color: string }>(({ color }) => ({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  backgroundColor: color,
  color: colorPalette.monotone[0],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11px',
  fontWeight: 'bold',
  flexShrink: 0,
}))

const ThumbnailContent = styled('div')({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px 12px 12px',
  pointerEvents: 'none',
  minWidth: 0,
})

const PageTitle = styled('div')({
  fontSize: '14px',
  fontWeight: 'bold',
  color: colorPalette.monotone[10],
  textAlign: 'center',
  wordBreak: 'break-all',
  lineHeight: 1.3,
  maxHeight: 'calc(1.3em * 3)',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
})

const ActionsWrapper = styled('div')({
  position: 'absolute',
  top: '6px',
  right: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
})

const TypeBadge = styled('div')<{
  color: string
  bg: string
  border: string
}>(({ color, bg, border }) => ({
  fontSize: '10px',
  fontWeight: 600,
  color,
  backgroundColor: bg,
  border: `1px solid ${border}`,
  borderRadius: '10px',
  padding: '1px 8px',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  userSelect: 'none',
}))

const ActionIconButton = styled(IconButton)({
  width: '22px',
  height: '22px',
  padding: 0,
  color: colorPalette.monotone[4],

  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
    color: colorPalette.monotone[10],
  },
})

const StyledMoreVertIcon = styled(MoreVertRoundedIcon)({
  fontSize: '16px',
})

const StyledCloseIcon = styled(CloseRoundedIcon)({
  fontSize: '16px',
})

const UnconfiguredBadge = styled('div')({
  position: 'absolute',
  bottom: '6px',
  left: '10px',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  fontSize: '10px',
  fontWeight: 600,
  color: colorPalette.pink[6],
  backgroundColor: colorPalette.pink[1],
  border: `1px solid ${colorPalette.pink[2]}`,
  borderRadius: '4px',
  padding: '1px 6px',
  lineHeight: 1.4,
  userSelect: 'none',
})

const UnconfiguredIcon = styled(ErrorOutlineRoundedIcon)({
  fontSize: '12px',
  color: colorPalette.pink[6],
})
